import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import cn from 'classnames';
import moment from 'moment';
import { useTranslation } from 'react-i18next';
import { Divider, Table, Select } from '_atoms';
import { TableMobile } from '_atoms/Table/Table';
import { Typography, TYPOGRAPHY_WEIGHT } from '_atoms/Typography';
import { ReactComponent as Vector } from 'dist/icons/Vector1.svg';
import { PLACEMENTS, SelectSmall } from '_atoms/SelectSmall';
import type { SelectSmallOption } from '_atoms/SelectSmall';
import type { TableRow } from '_atoms/Table/types';
import type { Opening } from 'store/reducers/jobReducer';
import type { AppDispatch } from 'store';
import { SELECT_TYPES } from '_atoms/Select/constants';
import { PopupApproveOpening, PopupCloseDeclineOpening } from '_molecules';
import useScreenResize from 'helpers/useScreenResize';
import {
  declineApproveOpening,
  getAllCompanies,
  getRMJobsTableDashboardAPI,
  getRMServicesTableDashboardAPI,
} from 'actions';
import { getCompanies } from 'store/selectors/commonSelectors';
import { ReactComponent as JobImage } from 'dist/emptyStates/dashboardOpening.svg';
import { EXPECTED_DATES } from '_templates/CreateOpening/constants';
import type {
  DashboardSelectOption as SelectOption,
  DashboardTableOptions as TableOptions,
  DashboardGroupingOption as GroupingOption,
  OpeningDeclinePopupState,
  OpeningApprovePopupState,
} from '_templates/dashboardTypes';
import { jobTableDataMapper, serviceTableDataMapper, isServiceGrouping } from './utils';
import {
  COLUMNS_OPENINGS_SERVICE,
  INITIAL_VALUES_1,
  INITIAL_VALUES_2,
  OPENINGS_GROUP,
  COLUMNS_OPENINGS_SERVICE_TOP,
  COLUMNS_OPENINGS_JOB_TOP,
  COLUMNS_OPENINGS_JOB,
  STATUSES,
  COMPANY_ALL,
} from './constants';
import './style.scss';

function OpeningsTableEmpty() {
  const { t } = useTranslation();

  return (
    <div className="all-openings__empty">
      <JobImage />
      <Typography.Title3 weight={TYPOGRAPHY_WEIGHT.BOLD}>
        {t('translation:dashboard_resource_manager.text_no_openings')}
      </Typography.Title3>
      <Typography.Text>{t('translation:dashboard_resource_manager.text_no_openings_hint')}</Typography.Text>
    </div>
  );
}

export const DashboardResourceManager = () => {
  const dispatch = useDispatch<AppDispatch>();
  const tableRef = useRef<HTMLDivElement>(null);
  const { t } = useTranslation();

  const [loading, setLoading] = useState(false);
  const [tableData, setTableData] = useState<Record<string, unknown>[]>([]);
  const [tableOptions, setTableOptions] = useState<TableOptions>(INITIAL_VALUES_1);
  const [openingStatus, setOpeningStatus] = useState<SelectOption[]>([STATUSES[1], STATUSES[7]]);
  const [company, setCompany] = useState<SelectSmallOption>(COMPANY_ALL);
  const [grouping, setGrouping] = useState<GroupingOption>({
    ...OPENINGS_GROUP[0],
    tableData: OPENINGS_GROUP[1].tableData,
  } as GroupingOption);
  const [expandedRowKeys, setExpandedRowKeys] = useState<string[]>([]);
  const [showOpeningDeclinePopup, setShowOpeningDeclinePopup] = useState<OpeningDeclinePopupState>({
    open: false,
  });
  const [openApprovePopup, setOpenApprovePopup] = useState<OpeningApprovePopupState>({ open: false });

  const companies = useSelector(getCompanies);
  const companyOptions = useMemo(
    () => [COMPANY_ALL, ...companies.map(c => ({ value: c.uuid, label: c.name }))],
    [companies]
  );

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const count = useMemo(
    () => tableData.reduce((sum, data) => sum + (data.openings as unknown[]).length, 0),
    [tableData]
  );

  function getTableData(group: GroupingOption = grouping, options: TableOptions = tableOptions) {
    setLoading(true);
    if (isServiceGrouping(group.value)) {
      getRMServicesTableDashboardAPI({
        limit: options.limit.value,
        offset: options.offset,
        status: openingStatus.map(({ value }) => value).join(','),
        company: company.value,
      })
        .then(d => {
          setTableOptions(o =>
            group.value !== group.tableData
              ? { ...INITIAL_VALUES_1, itemsCount: d.count }
              : { ...o, itemsCount: d.count }
          );
          const data = serviceTableDataMapper(d.results);
          setTableData(data as Record<string, unknown>[]);
          setGrouping(g => ({ ...g, tableData: g.value }));
          setExpandedRowKeys(data.map(({ uuid }) => uuid as string));
        })
        .catch(() => {
          setTableOptions(INITIAL_VALUES_1);
          setTableData([]);
          setGrouping(g => ({ ...g, tableData: g.value }));
        })
        .finally(() => setLoading(false));
    } else {
      getRMJobsTableDashboardAPI({
        limit: options.limit.value,
        offset: options.offset,
        status: openingStatus.map(({ value }) => value).join(','),
        company: company.value,
      })
        .then(d => {
          setTableOptions(o =>
            group.value !== group.tableData
              ? { ...INITIAL_VALUES_2, itemsCount: d.count }
              : { ...o, itemsCount: d.count }
          );
          const data = jobTableDataMapper(d.results);
          setTableData(data as Record<string, unknown>[]);
          setGrouping(g => ({ ...g, tableData: g.value }));
          setExpandedRowKeys(data.map(({ uuid }) => uuid as string));
        })
        .catch(() => {
          setTableOptions(INITIAL_VALUES_2);
          setTableData([]);
          setGrouping(g => ({ ...g, tableData: g.value }));
        })
        .finally(() => setLoading(false));
    }
  }

  const onExpand =
    ({ uuid }: Record<string, unknown>) =>
    () => {
      const rowUuid = uuid as string;
      if (!expandedRowKeys.includes(rowUuid)) setExpandedRowKeys(rows => [...rows, rowUuid]);
      else setExpandedRowKeys(rows => rows.filter(r => r !== rowUuid));
    };

  const onClickTitle =
    ({ uuid, name }: Record<string, unknown>) =>
    () => {
      if (isServiceGrouping(grouping.value))
        window.open(`/jobs?services=[{"value":"${uuid}","label":"${name}"}]`, '_blank', 'noopener noreferrer');
      else window.open(`/jobs/${uuid}/show/openings`, '_blank', 'noopener noreferrer');
    };

  const onChangeGrouping = (v: GroupingOption) => {
    setGrouping(v);
    getTableData(v);
  };

  const onChangeFilter = (field: string, item: unknown) => {
    if (field === 'page') {
      const options = {
        ...tableOptions,
        page: item as number,
        offset: ((item as number) - 1) * tableOptions.limit.value,
      };
      setTableOptions(options);
      getTableData(grouping, options);
    } else if (field === 'limit') {
      const options = { ...tableOptions, limit: item as { value: number; label: number }, offset: 0, page: 1 };
      setTableOptions(options);
      getTableData(grouping, options);
    } else {
      const options = { ...tableOptions, [field]: item };
      setTableOptions(options);
      getTableData(grouping, options);
    }
  };

  const onOpeningAction = ({ job_uuid, service_uuid, opening, ...d }: Record<string, unknown>, type: string) => {
    const openingData = opening as Record<string, unknown>;
    if (type === 'approve') {
      const show =
        Math.abs(moment(openingData.expected as string, 'YYYY-MM-DD').diff(moment(), 'days')) <
        Math.abs(EXPECTED_DATES[openingData.level as string]?.diff(moment(), 'days'));
      if (show) {
        setOpenApprovePopup({
          open: true,
          job_uuid: job_uuid as string,
          service_uuid: service_uuid as string,
          type,
        });
      } else {
        declineApproveOpening(job_uuid as string, service_uuid as string, type, {}).then(() => getTableData());
      }
    } else {
      setShowOpeningDeclinePopup({
        open: true,
        job_uuid: job_uuid as string,
        service_uuid: service_uuid as string,
        opening: opening as Opening,
        type,
        job_name: d.job_name as string | undefined,
      });
    }
  };

  const onCloseCloseOpeningPopup = () => setShowOpeningDeclinePopup({ open: false });
  const onDeclineOpening = (reason: string) => {
    if (!showOpeningDeclinePopup.open) return;
    const { job_uuid, service_uuid, type } = showOpeningDeclinePopup;
    declineApproveOpening(job_uuid, service_uuid, type, { reason }).then(() => getTableData());
    setShowOpeningDeclinePopup({ open: false });
  };

  const onCloseApproveConfirmation = () => setOpenApprovePopup({ open: false });
  const onApproveConfirmationPopup = () => {
    if (!openApprovePopup.open) return;
    declineApproveOpening(openApprovePopup.job_uuid, openApprovePopup.service_uuid, openApprovePopup.type, {}).then(
      () => getTableData()
    );
    setOpenApprovePopup({ open: false });
  };

  useEffect(() => {
    dispatch(getAllCompanies());
  }, [dispatch]);
  useEffect(getTableData, [openingStatus, company]);
  useScreenResize('md', tableRef);

  return (
    <div className="dashboard-resource-manager">
      <Table
        innerRef={tableRef}
        rowKey="uuid"
        className={cn('all-openings', grouping.tableData)}
        cardTitle={t('translation:dashboard_resource_manager.title')}
        cardCount={tableOptions.itemsCount}
        emptyComponent={<OpeningsTableEmpty />}
        extra={
          <div className="all-openings__header-extra">
            <Select
              labelInValue
              options={STATUSES}
              placeholder={t('translation:dashboard_resource_manager.ph_status')}
              value={openingStatus}
              onChange={setOpeningStatus}
              mode={SELECT_TYPES.MULTIPLE}
            />
            <div className="filter-container">
              <SelectSmall
                label={t('translation:dashboard_resource_manager.label_company')}
                options={companyOptions}
                value={company}
                onChange={setCompany}
                placement={PLACEMENTS.BOTTOM_RIGHT}
              />
            </div>
            <Divider type="vertical" />
            <div className="filter-container">
              <SelectSmall
                label={t('translation:dashboard_resource_manager.label_group_by')}
                options={OPENINGS_GROUP}
                value={grouping}
                onChange={onChangeGrouping}
                placement={PLACEMENTS.BOTTOM_RIGHT}
              />
            </div>
          </div>
        }
        showCardFooter={!!tableData.length}
        columns={isServiceGrouping(grouping.tableData) ? COLUMNS_OPENINGS_SERVICE_TOP : COLUMNS_OPENINGS_JOB_TOP}
        loading={loading}
        data={tableData as TableRow[]}
        limit={tableOptions.limit}
        page={tableOptions.page}
        itemsCount={tableOptions.itemsCount}
        updateParams={onChangeFilter}
        expandable={{
          expandedRowKeys,
          // eslint-disable-next-line react/no-unstable-nested-components
          expandedRowRender: ({ openings }: Record<string, unknown>) => (
            <>
              <Table
                rowKey="uuid"
                className={cn('all-openings__sub-table', grouping.tableData)}
                columns={
                  isServiceGrouping(grouping.tableData)
                    ? COLUMNS_OPENINGS_SERVICE(onOpeningAction)
                    : COLUMNS_OPENINGS_JOB(onOpeningAction)
                }
                data={openings as TableRow[]}
                showCardHeader={false}
                showCardFooter={false}
              />
              <TableMobile
                className={cn('all-openings__sub-table all-openings__sub-table-mobile', grouping.tableData)}
                data={openings as TableRow[]}
                columns={
                  isServiceGrouping(grouping.tableData)
                    ? COLUMNS_OPENINGS_SERVICE(onOpeningAction)
                    : COLUMNS_OPENINGS_JOB(onOpeningAction)
                }
              />
            </>
          ),
          // eslint-disable-next-line react/no-unstable-nested-components
          expandIcon: ({ expanded, record }) => (
            <div role="none" className="expanded-row">
              <div className="expanded-row__info">
                <Vector className={cn('expanded-row__icon', { expanded })} onClick={onExpand(record)} />
                <Typography.Text className="name" onClick={onClickTitle(record)}>
                  {record.name}
                  <span>{record.count}</span>
                </Typography.Text>
                {!!record.service && <Typography.Text className="name__extra">{record.service}</Typography.Text>}
              </div>
              <div className="expanded-row__extra">
                <Typography.Caption>
                  {(t as (key: string, options?: Record<string, unknown>) => string)(
                    'translation:dashboard_resource_manager.text_total_active_candidates',
                    { count: record.candidates_count as number }
                  )}
                </Typography.Caption>
                <Typography.Caption>
                  {(t as (key: string, options?: Record<string, unknown>) => string)(
                    'translation:dashboard_resource_manager.text_total_openings',
                    { count: record.openings_count as number }
                  )}
                </Typography.Caption>
              </div>
            </div>
          ),
        }}
      />
      <PopupCloseDeclineOpening
        open={showOpeningDeclinePopup.open}
        opening={showOpeningDeclinePopup.open ? showOpeningDeclinePopup.opening : undefined}
        jobName={showOpeningDeclinePopup.open ? showOpeningDeclinePopup.job_name ?? '' : ''}
        onClose={onCloseCloseOpeningPopup}
        confirm={onDeclineOpening}
      />
      <PopupApproveOpening
        open={openApprovePopup.open}
        onOk={onApproveConfirmationPopup}
        onClose={onCloseApproveConfirmation}
      />
    </div>
  );
};
